import { useLayoutEffect, useState } from 'react';

/** How long an element must stay intersecting before it counts as "actually
 *  seen" — see this hook's own doc for why a plain `isIntersecting` check
 *  alone isn't enough. */
const DEFAULT_SETTLE_MS = 100;

/**
 * True once `node` has been in the viewport (plus `rootMargin`)
 * continuously for `settleMs` — then stays true for the life of the
 * component, even if the element later scrolls back out of view.
 *
 * Takes the DOM node itself (typically from a callback ref stored in
 * state), not a `RefObject` — a plain `useRef` object never changes
 * identity, so an effect keyed on it cannot tell "the ref now points at a
 * different (or newly-created) element" from "nothing changed." Both
 * `ExerciseThumbnail` and `HistoryExerciseThumbnail` conditionally render
 * their `<img>` for the first time only once an async lookup resolves
 * (`exerciseSources`/`thumbnailExercise`) — with a `RefObject`, this hook's
 * effect ran once on mount, captured `ref.current === null` (the image
 * didn't exist yet), and never observed anything again once the image
 * finally appeared on a later render, permanently leaving the image
 * unloaded. A real browser's `IntersectionObserver` made this invisible to
 * every existing test: jsdom has none, so the "no IntersectionObserver"
 * fallback below resolved `visible` immediately regardless of the ref's
 * timing, masking the bug entirely (found via live-browser testing,
 * 2026-09-30).
 *
 * A bare `entries.some(e => e.isIntersecting)` is not enough on its own: a
 * fast, continuous scroll still passes every row's geometry through the
 * observed region for at least one frame, so `IntersectionObserver` reports
 * each of them as having intersected however briefly — confirmed directly by
 * scrolling a real list of 200+ rows, where a handful of large wheel ticks
 * left the large majority of skipped-past rows "intersected" and loaded
 * anyway. The settle delay is what turns "this row's bounding box crossed
 * the viewport at some point" into "the user is actually looking at this
 * row": intersecting starts a short timer, leaving the viewport before it
 * fires cancels it, and only a timer that survives to completion commits to
 * loading. This is the whole mechanism behind "scrolled past quickly → stays
 * unloaded; stopped/slowed down → loads".
 *
 * Exercise list rows mount once and are never unmounted while `visibleCount`
 * only grows (`ExerciseList.tsx`'s own incremental-loading design), so a
 * component-local "have I ever settled into view" flag is enough to satisfy
 * "loaded once, never reloaded on scrolling back" — no separate image cache
 * is needed; the browser's own HTTP cache already keeps a re-requested
 * catalogue GIF instant regardless.
 *
 * Falls back to `true` immediately when `IntersectionObserver` doesn't exist
 * — the same escape hatch `ExerciseList.tsx`'s own incremental loading
 * already uses for older browsers and for jsdom under test.
 */
export function useLazyVisible(
  node: Element | null,
  rootMargin = '160px',
  settleMs = DEFAULT_SETTLE_MS,
): boolean {
  const [visible, setVisible] = useState(false);

  // A layout effect, not a passive one: this only decides whether to *start*
  // observing (or to skip straight to visible when there's no
  // `IntersectionObserver` at all) — synchronous, node-driven setup is exactly
  // what layout effects are for, and it keeps the "no IntersectionObserver"
  // fallback resolving within the same commit as mount, rather than one tick
  // later (observably different to anything asserting the very first render).
  useLayoutEffect(() => {
    if (visible) return;
    if (typeof IntersectionObserver === 'undefined') {
      setVisible(true);
      return;
    }
    if (node === null) return;

    let settleTimer: ReturnType<typeof setTimeout> | null = null;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[entries.length - 1];
        if (entry === undefined) return;

        if (entry.isIntersecting) {
          // Already waiting on a timer from an earlier callback — don't
          // restart the clock, just let it keep running.
          if (settleTimer === null) {
            settleTimer = setTimeout(() => {
              settleTimer = null;
              setVisible(true);
            }, settleMs);
          }
        } else if (settleTimer !== null) {
          // Left the observed region before settling — this was a row
          // scrolled past, not one the user stopped on.
          clearTimeout(settleTimer);
          settleTimer = null;
        }
      },
      { rootMargin },
    );
    observer.observe(node);
    return () => {
      observer.disconnect();
      if (settleTimer !== null) clearTimeout(settleTimer);
    };
  }, [node, visible, rootMargin, settleMs]);

  return visible;
}
